import { useState, useMemo, useEffect, useRef } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Plus,
  SkipForward,
  Check,
  ChevronRight,
  AlertTriangle,
  Info,
  CheckCircle,
  Settings2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  Facet,
  FacetMappingDecision,
  MappingAction,
} from '@/types/interviewer'
import { FACET_TYPE_CONFIG, getMappingSummary } from '@/types/interviewer'
import { resolveEntityType } from '@/lib/interviewer/entityCreation'

interface OntologyMappingPanelProps {
  facets: Facet[]
  decisions: FacetMappingDecision[]
  onDecision: (facetId: string, action: MappingAction, data?: Partial<FacetMappingDecision>) => void
  onComplete: () => void
}

export function OntologyMappingPanel({
  facets,
  decisions,
  onDecision,
  onComplete,
}: OntologyMappingPanelProps) {
  const [showExceptions, setShowExceptions] = useState(false)

  // Identify potential matches (high confidence AI suggestions that might map to existing entities)
  const potentialMatches = useMemo(() => {
    return facets.filter(
      facet => facet.suggestedOntologyType && facet.confidence && facet.confidence > 0.7
    )
  }, [facets])

  // Get decision for a facet
  const getDecision = (facetId: string) => decisions.find(d => d.facetId === facetId)

  // Count stats
  const totalFacets = facets.length

  // Guard to prevent double-execution from React StrictMode or re-renders
  const hasAutoAppliedRef = useRef(false)

  // Auto-apply "create new" for all undecided facets on mount
  useEffect(() => {
    // Prevent multiple executions
    if (hasAutoAppliedRef.current) {
      console.log('[OntologyMappingPanel] Auto-apply already executed, skipping')
      return
    }

    console.log(`[OntologyMappingPanel] Auto-apply check: ${facets.length} facets, ${decisions.length} existing decisions`)

    if (decisions.length === 0 && facets.length > 0) {
      hasAutoAppliedRef.current = true
      console.log(`[OntologyMappingPanel] Auto-applying decisions for ${facets.length} facets`)

      // Auto-apply default decisions for all facets
      for (const facet of facets) {
        // Get the raw suggested type from AI or facet config
        const rawSuggestedType = facet.suggestedOntologyType ||
          FACET_TYPE_CONFIG[facet.type]?.ontologyTypes[0] || 'Problem'

        // Resolve to valid entity type
        const resolved = resolveEntityType(rawSuggestedType)
        const entityType = resolved.entityType || FACET_TYPE_CONFIG[facet.type]?.ontologyTypes[0] || 'Problem'

        const label = facet.content.slice(0, 100)

        console.log(`[OntologyMappingPanel] Creating decision for facet ${facet.id}:`, {
          type: facet.type,
          rawSuggestedType,
          resolvedType: entityType,
          isInsightOnly: resolved.isInsightOnly,
          label: label.slice(0, 30),
        })

        // Skip insight-only facets
        if (resolved.isInsightOnly) {
          onDecision(facet.id, 'skip', {
            facetId: facet.id,
            action: 'skip',
            decidedAt: new Date().toISOString(),
            aiConfidence: facet.confidence,
            aiSuggestedType: rawSuggestedType,
          })
        } else {
          onDecision(facet.id, 'accept_suggestion', {
            facetId: facet.id,
            action: 'accept_suggestion',
            decidedAt: new Date().toISOString(),
            aiConfidence: facet.confidence,
            aiSuggestedType: rawSuggestedType,
            newEntityType: entityType,
            newEntityLabel: label,
          })
        }
      }

      console.log(`[OntologyMappingPanel] Finished auto-applying ${facets.length} decisions`)
    }
  }, [facets, decisions.length, onDecision])

  // Summary stats
  const summary = getMappingSummary(decisions)

  // Handle changing a specific facet's decision
  const handleChangeDecision = (facet: Facet, action: MappingAction) => {
    const rawSuggestedType = facet.suggestedOntologyType ||
      FACET_TYPE_CONFIG[facet.type]?.ontologyTypes[0] || 'Problem'

    // Resolve to valid entity type
    const resolved = resolveEntityType(rawSuggestedType)
    const entityType = resolved.entityType || FACET_TYPE_CONFIG[facet.type]?.ontologyTypes[0] || 'Problem'

    onDecision(facet.id, action, {
      facetId: facet.id,
      action,
      decidedAt: new Date().toISOString(),
      aiConfidence: facet.confidence,
      aiSuggestedType: rawSuggestedType,
      newEntityType: action === 'skip' ? undefined : entityType,
      newEntityLabel: action === 'skip' ? undefined : facet.content.slice(0, 100),
    })
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with summary */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium">Ontology Mapping</h3>
          <p className="text-xs text-text-muted">
            {totalFacets} facets will be mapped to your ontology
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2 text-xs">
            <Badge variant="outline" className="bg-solution/10 border-solution text-solution">
              <Plus className="w-3 h-3 mr-1" />
              {summary.accepted + summary.created} new entities
            </Badge>
            {summary.skipped > 0 && (
              <Badge variant="outline">
                {summary.skipped} skipped
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main content - Summary view (default) */}
      <div className="flex-1 overflow-auto">
        {/* Quick action card */}
        <Card className="p-4 mb-4 bg-solution/5 border-solution">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-solution/20 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-solution" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-sm mb-1">All facets ready for mapping</h4>
              <p className="text-xs text-text-muted mb-3">
                By default, all {totalFacets} facets will create new ontology entities based on AI suggestions.
                Click "Complete Mapping" to proceed, or review exceptions below.
              </p>
              <Button onClick={onComplete} className="gap-2">
                <Check className="w-4 h-4" />
                Complete Mapping ({totalFacets} entities)
              </Button>
            </div>
          </div>
        </Card>

        {/* Potential matches warning */}
        {potentialMatches.length > 0 && (
          <Card className="p-4 mb-4 bg-warning/5 border-warning/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-sm mb-1">
                  {potentialMatches.length} potential matches detected
                </h4>
                <p className="text-xs text-text-muted mb-2">
                  These facets have high-confidence AI suggestions and may match existing entities in your ontology.
                  Consider reviewing them to avoid duplicates.
                </p>
                <div className="space-y-2">
                  {potentialMatches.slice(0, 3).map((facet) => (
                    <FacetQuickEdit
                      key={facet.id}
                      facet={facet}
                      decision={getDecision(facet.id)}
                      onChangeAction={(action) => handleChangeDecision(facet, action)}
                    />
                  ))}
                  {potentialMatches.length > 3 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowExceptions(true)}
                      className="text-xs"
                    >
                      View all {potentialMatches.length} potential matches
                      <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Toggle to show all facets */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowExceptions(!showExceptions)}
          className="mb-4 text-xs"
        >
          <Settings2 className="w-3.5 h-3.5 mr-1.5" />
          {showExceptions ? 'Hide' : 'Show'} all facets ({totalFacets})
        </Button>

        {/* All facets list (collapsed by default) */}
        {showExceptions && (
          <div className="space-y-2 mb-4">
            <div className="text-xs font-medium text-text-muted mb-2">All Facets</div>
            {facets.map((facet) => (
              <FacetQuickEdit
                key={facet.id}
                facet={facet}
                decision={getDecision(facet.id)}
                onChangeAction={(action) => handleChangeDecision(facet, action)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-warm-200 flex items-center justify-between">
        <div className="flex items-start gap-2 text-xs text-text-muted">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <p>
            New ontology entities will be created for each mapped facet.
            You can change individual mappings above if needed.
          </p>
        </div>
        <Button onClick={onComplete} className="gap-2">
          Complete Mapping
          <Check className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}

// Compact facet editor for quick changes
function FacetQuickEdit({
  facet,
  decision,
  onChangeAction,
}: {
  facet: Facet
  decision: FacetMappingDecision | undefined
  onChangeAction: (action: MappingAction) => void
}) {
  const typeConfig = FACET_TYPE_CONFIG[facet.type]
  const isSkipped = decision?.action === 'skip'

  return (
    <div className={cn(
      'flex items-center gap-3 p-2 rounded-lg border transition-colors',
      isSkipped ? 'bg-warm-50 border-warm-200 opacity-60' : 'bg-background border-warm-200'
    )}>
      {/* Type badge */}
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 flex-shrink-0"
        style={{ borderColor: typeConfig?.color, color: typeConfig?.color }}
      >
        {typeConfig?.label || facet.type}
      </Badge>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-xs truncate">{facet.content}</p>
      </div>

      {/* AI suggestion - show resolved entity type */}
      {facet.suggestedOntologyType && (() => {
        const resolved = resolveEntityType(facet.suggestedOntologyType)
        if (resolved.isInsightOnly) {
          return (
            <Badge variant="outline" className="text-[10px] bg-warm-100 border-warm-300 text-text-muted flex-shrink-0">
              insight only
            </Badge>
          )
        }
        return (
          <Badge variant="outline" className="text-[10px] bg-solution/10 border-solution/30 text-solution flex-shrink-0">
            → {resolved.entityType}
          </Badge>
        )
      })()}

      {/* Quick actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onChangeAction('accept_suggestion')}
          className={cn(
            'p-1.5 rounded transition-colors',
            !isSkipped ? 'bg-solution/10 text-solution' : 'hover:bg-warm-100 text-text-muted'
          )}
          title="Create new entity"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onChangeAction('skip')}
          className={cn(
            'p-1.5 rounded transition-colors',
            isSkipped ? 'bg-warm-200 text-text' : 'hover:bg-warm-100 text-text-muted'
          )}
          title="Skip this facet"
        >
          <SkipForward className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}
